import { InjectLocatesParamsSchema, LocateInjectionReceiptSchema } from 'src/queries/locatesV2/injection';
import { z } from 'zod';

const requiredIdSchema = (label: string) => z.string().trim().min(1, `${label} is required`);

export const LocateInjectionSymbolInputSchema = z
    .string()
    .trim()
    .min(1, 'Symbol is required')
    .transform((symbol) => symbol.toUpperCase());

export const LocateInjectionQuantityInputSchema = z
    .string()
    .trim()
    .regex(/^[1-9]\d*$/, 'Quantity must be a positive whole number')
    .transform(Number)
    .pipe(z.number().int().positive());

export const LocateInjectionPriceInputSchema = z
    .string()
    .trim()
    .regex(/^(?:\d+(?:\.\d{1,4})?|\.\d{1,4})$/, 'Price must be zero or greater with at most 4 decimals')
    .transform(Number)
    .pipe(z.number().finite().nonnegative());

export const SingleLocateInjectionDraftSchema = z
    .object({
        accountId: requiredIdSchema('Account'),
        providerId: requiredIdSchema('Provider'),
        symbol: LocateInjectionSymbolInputSchema,
        quantity: LocateInjectionQuantityInputSchema,
        pricePerShare: LocateInjectionPriceInputSchema,
    })
    .strict()
    .transform(({ accountId, providerId, symbol, quantity, pricePerShare }) => ({
        accountId,
        receipts: [
            {
                providerId,
                symbol,
                quantity,
                pricePerShare,
            },
        ],
    }))
    .pipe(InjectLocatesParamsSchema);

export const LocateInjectionCsvRowSchema = z
    .object({
        symbol: LocateInjectionSymbolInputSchema,
        quantity: LocateInjectionQuantityInputSchema,
        price: LocateInjectionPriceInputSchema,
    })
    .strict()
    .transform(({ symbol, quantity, price }) => ({
        symbol,
        quantity,
        pricePerShare: price,
    }));

export const CsvLocateInjectionSelectionSchema = z
    .object({
        accountId: requiredIdSchema('Account'),
        providerId: requiredIdSchema('Provider'),
    })
    .strict();

export function buildCsvInjectionParams({
    accountId,
    providerId,
    rows,
}: z.input<typeof CsvLocateInjectionSelectionSchema> & {
    rows: ReadonlyArray<z.output<typeof LocateInjectionCsvRowSchema>>;
}) {
    return InjectLocatesParamsSchema.safeParse({
        accountId,
        receipts: rows.map(({ symbol, quantity, pricePerShare }) => ({
            providerId,
            symbol,
            quantity,
            pricePerShare,
        })),
    });
}

export function validateLocateInjectionReceipt(value: unknown) {
    return LocateInjectionReceiptSchema.safeParse(value);
}
